import React, { useEffect, useState } from 'react'
import { DragDropContext } from 'react-beautiful-dnd'
import { Alert, Button, Col, Flex, Form, InputNumber, Row } from 'antd'
import UnmatchedValuesList from './UnmatchedValuesList'
import { DroppableInput } from './DroppableInput'
import TextArea from 'antd/es/input/TextArea'
import CategoriesForm from './CategoriesForm'

const QuestionWithRubricForm = ({
    onSubmit,
    originalValues = {},
    rubricValues: rubricValuesProp = {},
    reviewedValues = {},
    error,
    loading = false,
    unmatchedRubricValues: unmatchedRubricValuesProp = [],
    unmatchedRubricValuesIndex = 0,
    onUnmatchedRubricValuesChange = items => console.log('Items changed:', items),
    submitButtonText = 'Submit',
}) => {
    const [form] = Form.useForm()
    const [unmatchedRubricValues, setUnmatchedRubricValues] = useState(unmatchedRubricValuesProp)
    const [rubricValues, setRubricValues] = useState(rubricValuesProp)

    useEffect(() => {
        setUnmatchedRubricValues(unmatchedRubricValuesProp)
    }, [unmatchedRubricValuesProp])

    const handleRollbackToOriginal = fieldName => {
        const rubricValue = rubricValues[fieldName]
        const originalValue = originalValues[fieldName]
        if (rubricValue) {
            form.setFieldValue(fieldName, originalValue)

            const newUnmatchedItem = {
                id: `rejected-${fieldName}-${Date.now()}`,
                content: rubricValue,
            }

            // For simplicity, add the new item to the unmatchedRubricValuesIndex array
            const newArrays = [...unmatchedRubricValues]
            newArrays[unmatchedRubricValuesIndex] = [...(newArrays[unmatchedRubricValuesIndex] || []), newUnmatchedItem]

            setUnmatchedRubricValues(newArrays)
            onUnmatchedRubricValuesChange?.(newArrays)

            setRubricValues(prev => {
                const newSuggestions = { ...prev }
                delete newSuggestions[fieldName]
                return newSuggestions
            })
        }
    }

    const handleDragEnd = result => {
        if (!result.destination) return

        const { destination } = result

        if (destination.droppableId.startsWith('form-')) {
            const fieldName = destination.droppableId.replace('form-', '')
            const currentValue = form.getFieldValue(fieldName)

            // Determine the separator based on the field type
            const draggedItemContent = unmatchedRubricValues
                .flat()
                .find(item => String(item.id) === result.draggableId)?.content
            const isTextarea = ['content', 'gradingReferenceContent'].includes(fieldName)

            const mergedValue =
                currentValue && isTextarea ? `${currentValue}\n\n${draggedItemContent}` : draggedItemContent

            setRubricValues(prev => ({
                ...prev,
                [fieldName]: mergedValue,
            }))

            form.setFieldValue(fieldName, mergedValue)

            // Remove the item from unmatchedRubricValues
            const newArrays = unmatchedRubricValues.map(array =>
                array.filter(item => String(item.id) !== result.draggableId),
            )

            setUnmatchedRubricValues(newArrays)
            onUnmatchedRubricValuesChange?.(newArrays)
        }
    }

    const handleSubmit = async values => {
        if (onSubmit)
            onSubmit({
                ...values,
                categories: originalValues.categories.map((category, index) => ({
                    ...category,
                    name: values[`category-${index}-name`] || category.name,
                    feedback: values[`category-${index}-feedback`] || category.feedback,
                    grade: values[`category-${index}-grade`] || category.grade,
                })),
            })
    }

    const getInitialValue = fieldName =>
        reviewedValues[fieldName] || rubricValues[fieldName] || originalValues[fieldName]

    const handleRevert = () => {
        form.setFieldsValue({
            name: getInitialValue('name'),
            content: getInitialValue('content'),
            gradingReferenceContent: getInitialValue('gradingReferenceContent'),
            wordsLimit: getInitialValue('wordsLimit'),
            ...originalValues.categories.reduce((acc, category, index) => {
                acc[`category-${index}-name`] = category.name
                acc[`category-${index}-feedback`] = category.feedback
                acc[`category-${index}-grade`] = category.grade
                return acc
            }, {}),
        })

        setRubricValues(rubricValuesProp)
        setUnmatchedRubricValues(unmatchedRubricValuesProp)
        onUnmatchedRubricValuesChange?.(unmatchedRubricValuesProp)
    }

    return (
        <DragDropContext onDragEnd={handleDragEnd}>
            <Row gutter={[16, 16]}>
                <Col span={12}>
                    <Form
                        form={form}
                        name='question-form'
                        autoComplete='off'
                        onFinish={handleSubmit}
                        requiredMark={false}
                        layout='vertical'
                    >
                        <Form.Item
                            name='name'
                            label='Name'
                            rules={[{ required: true, message: 'Please, enter name' }]}
                            initialValue={getInitialValue('name')}
                        >
                            <DroppableInput
                                droppableId='form-name'
                                placeholder='Enter name'
                                autoSize
                                originalValue={originalValues?.name}
                                rubricValue={rubricValues?.name}
                                hasRubricValue={Boolean(rubricValues?.name)}
                                onRollbackToOriginal={() => handleRollbackToOriginal('name')}
                            />
                        </Form.Item>
                        <Form.Item
                            name='content'
                            label='Content'
                            rules={[{ required: true, message: 'Please, enter content' }]}
                            initialValue={getInitialValue('content')}
                        >
                            <DroppableInput
                                droppableId='form-content'
                                placeholder='Enter content'
                                autoSize
                                originalValue={originalValues?.content}
                                hasRubricValue={Boolean(rubricValues?.content)}
                                onRollbackToOriginal={() => handleRollbackToOriginal('content')}
                                inputComponent={TextArea}
                            />
                        </Form.Item>
                        <Form.Item
                            name='gradingReferenceContent'
                            label='Grading Reference Content'
                            initialValue={getInitialValue('gradingReferenceContent')}
                        >
                            <DroppableInput
                                droppableId='form-gradingReferenceContent'
                                placeholder='Enter grading reference content'
                                autoSize
                                originalValue={originalValues?.gradingReferenceContent}
                                hasRubricValue={Boolean(rubricValues.gradingReferenceContent)}
                                onRollbackToOriginal={() => handleRollbackToOriginal('gradingReferenceContent')}
                                inputComponent={TextArea}
                            />
                        </Form.Item>
                        <Form.Item
                            name='wordsLimit'
                            label='Word Limit'
                            rules={[{ type: 'number', min: 0 }]}
                            initialValue={getInitialValue('wordsLimit')}
                        >
                            <InputNumber addonAfter='words' step={10} />
                        </Form.Item>
                        <CategoriesForm
                            originalCategories={originalValues?.categories}
                            reviewedCategories={reviewedValues?.categories}
                            rubricCategories={rubricValues?.categories}
                        />
                        {error && <Alert message={error} type='error' showIcon closable />}
                        <Flex justify='end' gap={8}>
                            <Button danger onClick={handleRevert}>
                                Revert
                            </Button>
                            <Form.Item>
                                <Button type='primary' htmlType='submit' loading={loading}>
                                    {submitButtonText}
                                </Button>
                            </Form.Item>
                        </Flex>
                    </Form>
                </Col>
                <Col span={12}>
                    <div
                        style={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <div
                            style={{
                                flex: 1,
                                overflow: 'auto',
                                maxHeight: 'calc(100vh - 48px)',
                                position: 'sticky',
                                top: '24px',
                            }}
                        >
                            <UnmatchedValuesList
                                unmatchedRubricValues={unmatchedRubricValues}
                                initialIndex={unmatchedRubricValuesIndex}
                            />
                        </div>
                    </div>
                </Col>
            </Row>
        </DragDropContext>
    )
}

export default QuestionWithRubricForm
